//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  BVSamplingProduct,
  SamplingSocialTask,
} from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import { SamplingCatalogProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiV2Model';
import { NO_PDP_URL } from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import {
  EditableBVSamplingProduct,
  EditableBVSamplingProductVariation,
} from '@campaigns/types/sampling/SamplingProductTypes';
import { validateNumber } from '@campaigns/utils/FormUtils';
import { isDefined, isEmptyValue } from '@utils/filterPredicates';

export const getAllocatedUnits = (
  product: BVSamplingProduct,
  task: SamplingSocialTask,
): number =>
  product.variations?.reduce(
    (total, variation) =>
      total +
      (variation.allocations?.reduce(
        (allocationTotal, allocation) =>
          allocation.tasks.includes(task)
            ? allocationTotal + allocation.quantity
            : allocationTotal,
        0,
      ) ?? 0),
    0,
  ) ?? 0;

/**
 * Checks if a product has missing basic details.
 *
 * @param product - The product to check.
 * @returns True if any basic detail is missing, false otherwise.
 */
const hasMissingBasicDetails = (
  product: BVSamplingProduct | EditableBVSamplingProduct,
): boolean => {
  const requiredFields: (string | number | undefined)[] = [
    product.brand,
    product.category,
    product.description,
    product.imgUrl,
    product.name,
    product.targetCatalogProductId,
    product.quantity,
  ];

  return requiredFields.some(isEmptyValue);
};

/**
 * Checks if any variation of a product has missing details.
 *
 * @param product - The product to check.
 * @returns True if any variation detail is missing, false otherwise.
 */
const hasMissingVariationDetails = (
  product: BVSamplingProduct | EditableBVSamplingProduct,
): boolean => {
  if (!product.variations || product.variations.length === 0) {
    return true;
  }

  return product.variations.some((variation) => {
    const requiredFields: (string | number | undefined)[] = [
      variation.logisticsRef,
      variation.fairMarketValue,
      variation.quantity,
      variation.variantName,
      variation.imgUrl,
    ];

    return requiredFields.some(isEmptyValue);
  });
};

/**
 * Checks if the dimensions of a product are missing.
 *
 * @param product - The product to check.
 * @returns True if any dimension is missing, false otherwise.
 */
export const hasMissingDimensions = (product: BVSamplingProduct): boolean => {
  if (product.dimensions) {
    const { depth, format, height, width } = product.dimensions;
    return (
      isEmptyValue(format) ||
      isEmptyValue(width) ||
      isEmptyValue(height) ||
      isEmptyValue(depth)
    );
  }
  return true;
};

/**
 * Checks if a product has any missing details based on the box type.
 *
 * @param product - The product to check.
 * @returns True if any detail is missing, false otherwise.
 */
export const isMissingDetails = (
  product: BVSamplingProduct | EditableBVSamplingProduct | undefined,
): boolean => {
  if (!product) {
    return true;
  }

  return hasMissingBasicDetails(product) || hasMissingVariationDetails(product);
};

/**
 * Products without variants keep a single default variation, so only more
 * than one counts as having variants.
 */
export const hasVariants = (product: BVSamplingProduct): boolean =>
  (product.variations?.length ?? 0) > 1;

export const mapCatalogProductToBVSamplingProduct = (
  catalogProduct: SamplingCatalogProduct,
): BVSamplingProduct => {
  const transformedProduct: BVSamplingProduct = {
    brand: catalogProduct.brand,
    category: catalogProduct.category,
    description: catalogProduct.description,
    imgUrl: catalogProduct.imageUrl,
    name: catalogProduct.name,
    newProduct: false, // We need to set this to false to indicate that the product is not new and it's coming from the catalog
    pdpUrl:
      isEmptyValue(catalogProduct.productDetailPageUrl) ||
      catalogProduct.productDetailPageUrl === NO_PDP_URL
        ? undefined
        : catalogProduct.productDetailPageUrl,
    targetCatalogProductId: catalogProduct.id,
    variations: [
      {
        fairMarketValue: catalogProduct.fmv
          ? Number(catalogProduct.fmv)
          : undefined,
        quantity: undefined,
        variantName: undefined,
        logisticsRef: undefined,
        imgUrl: catalogProduct.imageUrl,
      },
    ],
  };

  const upcs = catalogProduct.upcs ?? [];
  const eans = catalogProduct.eans ?? [];
  const totalProductIdentifiers = upcs.length + eans.length;

  if (totalProductIdentifiers > 0 && transformedProduct.variations?.[0]) {
    if (totalProductIdentifiers === 1) {
      // If there's only one identifier, populate it to the GTIN/EAN/UPC field
      transformedProduct.variations[0].logisticsRef = upcs[0] ?? eans[0];
    } else {
      // If there are multiple identifiers, populate them to the GTIN/EAN/UPC for Syndication
      transformedProduct.destinationGTIN = upcs.concat(eans);
    }
  }

  return transformedProduct;
};

const transformDestinationGTIN = (
  destinationGTIN: string | undefined,
): string[] => {
  return destinationGTIN?.split(',').filter((gtin) => gtin.trim() !== '') ?? [];
};

export const prepareProductToSubmit = (
  data: EditableBVSamplingProduct,
): BVSamplingProduct => {
  const productToSubmit: BVSamplingProduct = {
    ...data,
    pdpUrl:
      isEmptyValue(data.pdpUrl) || data.pdpUrl === NO_PDP_URL
        ? undefined
        : data.pdpUrl,
    destinationGTIN: transformDestinationGTIN(data.destinationGTIN),
    dimensions: data.dimensions && {
      ...data.dimensions,
      depth: toNumber(data.dimensions.depth),
      height: toNumber(data.dimensions.height),
      width: toNumber(data.dimensions.width),
    },
    quantity: toNumber(data.quantity),
    variations: data.variations.map((variation) => ({
      ...variation,
      fairMarketValue: toNumber(variation.fairMarketValue),
      quantity: toNumber(variation.quantity),
    })),
  };

  if (
    productToSubmit.variations?.[0] &&
    productToSubmit.variations.length === 1
  ) {
    const variation = productToSubmit.variations[0];
    variation.imgUrl = productToSubmit.imgUrl ?? variation.imgUrl;
    variation.variantName = productToSubmit.name ?? variation.variantName;
  }

  productToSubmit.quantity =
    productToSubmit.variations?.reduce(
      (acc, variation) => acc + (variation.quantity ?? 0),
      0,
    ) ?? 0;

  return productToSubmit;
};

const toNumber = (value: string | undefined): number | undefined => {
  if (!isDefined(value) || value.trim() === '') {
    return undefined;
  }
  const num = Number(value);
  return isNaN(num) ? undefined : num;
};

/**
 * Replaces the variations of a product with the ones edited in the variants
 * drawer, keeping the product quantity in sync with the variant quantities.
 */
export const applyVariationsToProduct = (
  product: BVSamplingProduct,
  variations: EditableBVSamplingProductVariation[],
): BVSamplingProduct => {
  const updatedVariations = variations.map((variation) => ({
    ...variation,
    fairMarketValue: toNumber(variation.fairMarketValue),
    quantity: toNumber(variation.quantity),
  }));

  return {
    ...product,
    quantity: updatedVariations.reduce(
      (total, variation) => total + (variation.quantity ?? 0),
      0,
    ),
    variations: updatedVariations,
  };
};

const convertToValidString = (
  value: number | undefined,
): string | undefined => {
  return isDefined(value) && validateNumber(value) ? String(value) : undefined;
};

export const getInitialProduct = (
  product: BVSamplingProduct | undefined,
): EditableBVSamplingProduct => {
  const initialProduct = {
    ...product,
    destinationGTIN: product?.destinationGTIN?.join(',') ?? '',
    dimensions: product?.dimensions && {
      ...product.dimensions,
      depth: convertToValidString(product.dimensions.depth),
      height: convertToValidString(product.dimensions.height),
      width: convertToValidString(product.dimensions.width),
    },
    pdpUrl: product?.pdpUrl === NO_PDP_URL ? undefined : product?.pdpUrl,
    quantity: convertToValidString(product?.quantity),
    variations:
      product?.variations && product.variations.length > 0
        ? product.variations.map((variation, index) => ({
            ...variation,
            fairMarketValue: convertToValidString(variation.fairMarketValue),
            quantity: convertToValidString(
              variation.quantity ??
                (index === 0 ? product.quantity : undefined),
            ),
          }))
        : [
            {
              quantity: convertToValidString(product?.quantity),
              variantName: '',
            },
          ],
  };

  return initialProduct;
};

export const mergeProductsInCampaignRequest = (
  existingProducts: BVSamplingProduct[] = [],
  newProducts: BVSamplingProduct[],
): BVSamplingProduct[] => {
  const existingProductMap = new Map(
    existingProducts.map((product) => [
      product.targetCatalogProductId,
      product,
    ]),
  );

  newProducts.forEach((newProduct) => {
    existingProductMap.set(newProduct.targetCatalogProductId, newProduct);
  });

  return Array.from(existingProductMap.values());
};
